import type { Link, Root, Text } from "mdast"
import { findAndReplace } from "mdast-util-find-and-replace"
import { visit } from "unist-util-visit"

import {
  coviEntityRelativePath,
  isCoviEntityKind,
  type CoviEntityRef,
} from "./actionRoutes"
import { buildCoviOrganizationPath } from "./organizationPath"

interface EntityLinkOptions {
  organizationSlug: string | null
  workspaceSlug?: string | null
}

/**
 * Sofia cites an entity by emitting a typed reference token inside its markdown.
 * The token carries the entity KIND so the FE links it reliably
 * rather than guessing at opaque ids in prose:
 *
 *   coval://simulation/abc123
 *   coval://conversation/xyz789?turn=4
 *   coval://run/run_42
 *   coval://metric/m_latency
 *
 * This is the FE half of a lockstep contract with Sofia's chat API
 * (`product/instructions.py` teaches Sofia to cite ids in this form). The scheme
 * is opaque and app-internal — `linkifyCoviEntitiesPlugin` rewrites it to a real
 * org-scoped route; a token the FE can't resolve is left as literal text.
 */

/**
 * Matches a `coval://<kind>/<id>` token with an optional `?turn=<n>`. The id run
 * stops at whitespace and the closing punctuation Sofia tends to wrap ids in
 * (parentheses, brackets, quotes) so a citation links cleanly. Trailing sentence
 * punctuation (`.,;:!`) is trimmed in `parseCoviEntityRef` rather than the regex
 * so an id may still contain a `.` internally. `turn` is kept out of the id
 * capture so a bare id and a turn-anchored id share one resolution path.
 */
const ENTITY_URI_RE = /coval:\/\/([a-z_]+)\/([^\s)\]}"'?]+)(?:\?turn=(\d+))?/gi
const ENTITY_LINK_RE =
  /^coval:\/\/([a-z_]+)\/([^\s)\]}"'?]+)(?:\?turn=(\d+))?$/i

/** Trailing sentence punctuation to strip off a captured id (never mid-id). */
const TRAILING_PUNCTUATION_RE = /[.,;:!]+$/

/**
 * Parse one `coval://…` token into a typed `CoviEntityRef`, or null when the
 * kind is unknown or the id is empty. `turn` is only kept when it is a
 * non-negative integer. The regex groups arrive already URI-shaped; the id is
 * decoded so an encoded id round-trips back to its raw value.
 */
export function parseCoviEntityRef(
  kindRaw: string,
  idRaw: string,
  turnRaw?: string,
): CoviEntityRef | null {
  const kind = kindRaw.toLowerCase()
  if (!isCoviEntityKind(kind)) {
    return null
  }
  // Strip trailing sentence punctuation the citation may abut (e.g. a token
  // ending a sentence: "coval://run/run_7.") before decoding.
  const trimmedIdRaw = idRaw.replace(TRAILING_PUNCTUATION_RE, "")
  let id: string
  try {
    id = decodeURIComponent(trimmedIdRaw)
  } catch {
    // Malformed percent-encoding — treat the raw run as the id rather than throw.
    id = trimmedIdRaw
  }
  if (id.length === 0) {
    return null
  }
  const turn = turnRaw !== undefined ? Number.parseInt(turnRaw, 10) : undefined
  return {
    kind,
    id,
    ...(turn !== undefined && Number.isInteger(turn) && turn >= 0
      ? { turn }
      : {}),
  }
}

/**
 * remark plugin that rewrites Sofia's `coval://<kind>/<id>` citation tokens into
 * real markdown links pointing at the org-scoped detail page. It runs
 * inside `<Streamdown>` on every assistant reply, so a cited sim / conversation
 * / run / metric id renders as a clickable "teleport" link the user can follow
 * straight to the example.
 *
 * Bare tokens and typed link destinations share the scoped resolver. Authored labels, ordinary links, and code spans are preserved; unknown destinations remain subject to the markdown renderer's URL safety checks.
 *
 * The link's href is organization/workspace-scoped via `buildCoviOrganizationPath`.
 */
export function linkifyCoviEntitiesPlugin({
  organizationSlug: orgSlug,
  workspaceSlug,
}: EntityLinkOptions): (tree: Root) => void {
  const linkLabel = (ref: CoviEntityRef): string =>
    ref.turn !== undefined ? `${ref.id} · turn ${ref.turn}` : ref.id

  const resolveHref = (
    kind: string,
    id: string,
    turn?: string,
  ): { ref: CoviEntityRef; href: string } | null => {
    if (!orgSlug) {
      return null
    }
    const ref = parseCoviEntityRef(kind, id, turn)
    if (!ref) {
      return null
    }
    const href = buildCoviOrganizationPath(
      coviEntityRelativePath(ref),
      orgSlug,
      workspaceSlug,
    )
    return href ? { ref, href } : null
  }

  return (tree: Root) => {
    visit(tree, "link", (node) => {
      const match = ENTITY_LINK_RE.exec(node.url)
      if (!match) {
        return
      }
      const resolved = resolveHref(match[1], match[2], match[3])
      if (resolved) {
        node.url = resolved.href
      }
    })
    findAndReplace(tree, [
      [
        ENTITY_URI_RE,
        (
          _match: string,
          kind: string,
          idWithPunctuation: string,
          turn?: string,
        ): Array<Link | Text> | false => {
          const resolved = resolveHref(kind, idWithPunctuation, turn)
          if (!resolved) {
            return false
          }
          const { ref, href } = resolved
          const link: Link = {
            type: "link",
            url: href,
            title: null,
            children: [{ type: "text", value: linkLabel(ref) }],
          }
          // Trailing sentence punctuation the token abutted (stripped from the
          // id by parseCoviEntityRef) is preserved as text AFTER the link, so
          // "…coval://run/run_7." keeps its period outside the link.
          const trailing = TRAILING_PUNCTUATION_RE.exec(idWithPunctuation)?.[0]
          return trailing
            ? [link, { type: "text", value: trailing } satisfies Text]
            : [link]
        },
      ],
    ])
  }
}
